import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  InputData,
  LectureVideoCaption,
  MyClassService,
  selectActiveSubsection,
  selectCourseContent,
  TranscriptService,
} from '@shared';
import { ThkVideoPlayerComponent } from '@shared/components/learning-lab/thk-video-player/thk-video-player.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BehaviorSubject, combineLatest, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { LectureDetailV2, PartialEntity, ProcessingDirective, RouterStoreService, ToastService } from 'thkee-common';
// declare var videojs: any; // Declare videojs as a global variable

@UntilDestroy()
@Component({
  selector: 'app-learning-lab-activity-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  standalone: true,
  imports: [NgIf, AngularSvgIconModule, ProcessingDirective, ThkVideoPlayerComponent],
})
export class LearninglabVideoComponent implements OnInit {
  @Output() event = new EventEmitter<{ action: string; data: any }>();
  @Input() hidePrev: boolean = false;
  @Input() hideNext: boolean = false;
  @Input() prevData: any = null;
  @Input() nextData: any = null;
  isLoading: boolean = false;
  @ViewChild('video') videoElement!: ThkVideoPlayerComponent;

  data?: PartialEntity<LectureDetailV2>;
  sources$ = new BehaviorSubject<any>({});
  related_videos = [
    {
      thumb: '//domain.com/path/to/video_thumb_1.jpg',
      url: '//domain.com/path/to/video_1.html',
      title: 'Video 1 title',
      duration: '05:30',
    },
    {
      thumb: '//domain.com/path/to/video_thumb_2.jpg',
      url: '//domain.com/path/to/video_2.html',
      title: 'Video 2 title',
      duration: '05:30',
    },
    {
      thumb: '//domain.com/path/to/video_thumb_3.jpg',
      url: '//domain.com/path/to/video_3.html',
      title: 'Video 3 title',
      duration: '05:30',
    },
    {
      thumb: '//domain.com/path/to/video_thumb_4.jpg',
      url: '//domain.com/path/to/video_4.html',
      title: 'Video 4 title',
      duration: '05:30',
    },
    {
      thumb: '//domain.com/path/to/video_thumb_5.jpg',
      url: '//domain.com/path/to/video_5.html',
      title: 'Video 5 title',
      duration: '05:30',
    },
    {
      thumb: '//domain.com/path/to/video_thumb_6.jpg',
      url: '//domain.com/path/to/video_6.html',
      title: 'Video 6 title',
      duration: '05:30',
    },
  ];

  counter: number = 3;
  params: Params = {};
  isLoadingSource: boolean = true;
  videoTimelinePosition = 0;
  shareUrl: string = '';

  constructor(
    private el: ElementRef,
    private store: Store,
    private myClassService: MyClassService,
    private routerStore: RouterStoreService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private transcriptService: TranscriptService,
    private meta: Meta,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    const isPreview = this.route.snapshot.queryParams['preview'];
    combineLatest([this.routerStore.getQuery('preview'), this.routerStore.getParams()])
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(([preview, params]) => {
        this.params = params;
        if (isPreview) {
          this.shareUrl = window.location.host + `/course/${this.params['courseId']}?preview=true`;
        } else {
          this.shareUrl = window.location.host + `/course/${this.params['courseId']}`;
        }
      });

    this.store
      .select(selectActiveSubsection)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((res) => {
        if (this.params['overviewcode'] && this.params['courseId']) {
          setTimeout(() => {
            this.getVideoPosition(isPreview, this.params);
          }, 500);
        }
      });

    // Social share
    setTimeout(() => {
      firstValueFrom(this.store.select(selectCourseContent)).then((course) => {
        const courseTitle = course.courseOrerview.title;
        const courseDescription = course.courseOrerview.desc;
        const courseImage = course.courseOrerview.image_url;
        const courseUrl = window.location.href;

        // Set dynamic title
        this.titleService.setTitle(courseTitle);

        // Set dynamic meta tags
        this.meta.updateTag({ name: 'description', content: courseDescription });
        this.meta.updateTag({ property: 'og:title', content: courseTitle });
        this.meta.updateTag({ property: 'og:description', content: courseDescription });
        this.meta.updateTag({ property: 'og:image', content: courseImage });
        this.meta.updateTag({ property: 'og:url', content: courseUrl });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ name: 'twitter:card', content: courseTitle });
        this.meta.updateTag({ name: 'twitter:title', content: courseTitle });
        this.meta.updateTag({ name: 'twitter:description', content: courseDescription });
        this.meta.updateTag({ name: 'twitter:image', content: courseImage });
        this.meta.updateTag({ name: 'twitter:url', content: courseUrl });
      });
    }, 1000);
  }

  lectureVideoCaptions: LectureVideoCaption[] = [];
  getVideoPosition(preview: string | undefined | unknown, params: Params = {}) {
    firstValueFrom(this.store.select(selectActiveSubsection)).then((subsection) => {
      if (
        params['overviewcode'] &&
        params['courseId'] &&
        subsection?.type === 'lecture' &&
        subsection.lecture?.type === 'video'
      ) {
        // Video position
        this.myClassService
          .getVideoPosition(params['overviewcode'], subsection?.id || '')
          .pipe(distinctUntilChanged(), untilDestroyed(this))
          .subscribe({
            next: (res) => {
              this.videoTimelinePosition = res.watched_duration;
            },
            error: ({ error }) => {
              this.toastService.error({
                message: error.error,
              });
            },
          });

        // Video captions
        this.myClassService
          .getVideoCaptions(params['courseId'], subsection?.lecture?.id || '')
          .pipe(distinctUntilChanged(), untilDestroyed(this))
          .subscribe({
            next: (res) => {
              this.lectureVideoCaptions = res;
            },
            error: ({ error }) => {
              this.toastService.error({
                message: error.error,
              });
            },
          });

        // Transcoded video info
        this.data = subsection?.lecture;
        if (preview) {
          if (this.data?.id) {
            this.getPreviewTranscodedInfo(this.data?.id);
          }
        } else {
          if (this.data?.id) {
            this.getTranscodedInfo(params['courseId'], this.data?.id);
          }
        }
      }
    });
  }

  timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  intervalId: ReturnType<typeof setInterval> | undefined = undefined;

  videoEvent(event: { action: string; data: InputData }) {
    console.log('🚀 ~ LearninglabVideoComponent ~ videoEvent ~ event:', event);
    this.emitEvent(event.action, event.data);
    this.transcriptService.setVideoPosition(event.data.currentTime || 0);
    if (event.action === 'play') {
      this.counter = 3;
      this.isLoading = false;

      // Clear interval if it exists (prevent multiple intervals)
      if (this.intervalId !== undefined) {
        clearInterval(this.intervalId);
        this.intervalId = undefined;
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
      }
    } else if (event.action === 'ended') {
      this.counter = 3;
      this.isLoading = true;

      // Clear existing intervals/timeouts
      if (this.intervalId !== undefined) {
        clearInterval(this.intervalId);
        this.intervalId = undefined;
      }
      if (this.timeoutId !== undefined) {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
      }

      // Start countdown
      this.intervalId = setInterval(() => {
        this.counter--;
        if (this.counter === 0) {
          clearInterval(this.intervalId);
          this.intervalId = undefined;

          // Delay the transition after counter reaches 0
          this.timeoutId = setTimeout(() => {
            this.isLoading = false;
            this.emitEvent('next', { subsectionId: this.data?.section });
          }, 1000); // show "0" for 1 second
        }
      }, 1000);
    }
  }

  onPlay() {
    // Cancel the setTimeout if it's running
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    // Clear the interval if it's running
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.videoElement.onPlay();
  }

  emitEvent(action: string, data: any = {}) {
    this.event.emit({ action, data });
  }

  // Get transcoded video info
  private getTranscodedInfo(productId: string, lectureId: string) {
    this.isLoadingSource = true;
    this.myClassService
      .getLectureTranscodedInfo(productId, lectureId)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.sources$.next(res);
          this.isLoadingSource = false;
        },
        error: (err) => {
          this.isLoadingSource = false;
          console.log(err);
        },
      });
  }

  // Get transcoded video preview info
  private getPreviewTranscodedInfo(lectureId: string) {
    this.isLoadingSource = true;
    this.myClassService
      .getPreviewLectureTranscodedInfo(lectureId)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.sources$.next(res);
          this.isLoadingSource = false;
        },
        error: (err) => {
          this.isLoadingSource = false;
          console.log(err);
        },
      });
  }
}
